/**
 * Timezone-aware date formatting utility for TRIVONT SupportOne.
 * All dates in the app are stored as UTC. This module converts them
 * to the tenant's configured timezone for display.
 *
 * Usage:
 *   import { formatDate, formatDateTime, formatRelative, getAppTimezone, setAppTimezone } from '../utils/timezone';
 *   formatDateTime(ticket.created_at)   // "19 Mar 2026, 9:08 AM IST"
 *   formatDate(ticket.created_at)       // "19 Mar 2026"
 */

let _tz = 'Asia/Kolkata'; // default, updated from user profile

export function setAppTimezone(tz) {
  if (tz) _tz = tz;
}

export function getAppTimezone() {
  return _tz;
}

/** Get a short timezone label like "IST", "EST", "UTC" */
export function getTzLabel(tz = _tz) {
  try {
    const label = new Intl.DateTimeFormat('en', { timeZoneName: 'short', timeZone: tz })
      .formatToParts(new Date())
      .find(p => p.type === 'timeZoneName');
    return label?.value || tz;
  } catch {
    return 'UTC';
  }
}

/** Format: "19 Mar 2026, 9:08 AM IST" */
export function formatDateTime(isoString, tz = _tz) {
  if (!isoString) return '—';
  try {
    const d = new Date(isoString);
    const formatted = d.toLocaleString('en-IN', {
      timeZone: tz,
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
    return `${formatted} ${getTzLabel(tz)}`;
  } catch {
    return new Date(isoString).toLocaleString();
  }
}

/** Format: "19 Mar 2026" */
export function formatDate(isoString, tz = _tz) {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleDateString('en-IN', {
      timeZone: tz, day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return new Date(isoString).toLocaleDateString();
  }
}

/** Format: "9:08 AM" */
export function formatTime(isoString, tz = _tz) {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleTimeString('en-IN', {
      timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch {
    return new Date(isoString).toLocaleTimeString();
  }
}

/** "2 days ago", "just now", "in 3 hours" */
export function formatRelative(isoString) {
  if (!isoString) return '—';
  const diff = Date.now() - new Date(isoString).getTime();
  const abs = Math.abs(diff);
  const sign = diff > 0 ? -1 : 1;
  const mins  = Math.floor(abs / 60000);
  const hours = Math.floor(abs / 3600000);
  const days  = Math.floor(abs / 86400000);
  if (abs < 60000)   return 'just now';
  if (hours < 1)     return `${mins}m ago`;
  if (days < 1)      return `${hours}h ago`;
  if (days < 30)     return `${days}d ago`;
  return formatDate(isoString);
}

/** Duration from ms: "1d 4h 32m" */
export function formatDuration(ms) {
  if (!ms && ms !== 0) return '—';
  const abs = Math.abs(ms);
  const d = Math.floor(abs / 86400000);
  const h = Math.floor((abs % 86400000) / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const parts = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  if (m > 0 || parts.length === 0) parts.push(`${m}m`);
  return parts.join(' ');
}
